import React, { useState, useEffect } from 'react';
import { FormInput } from '../../../FormInput';
import { FormSelect } from '../../../FormSelect';
import { FormCheckbox } from '../../../FormCheckbox';
import { FormRadioGroup } from '../../../FormRadioGroup';
import Button from '../../../Button';
import { getNavigationIcon } from '../../../navigation/NavigationIcons';

const DOCUMENT_CATEGORIES = [
  { value: 'waiver', label: 'Waiver' },
  { value: 'medical', label: 'Medical' },
  { value: 'registration', label: 'Registration' },
  { value: 'emergency_contact', label: 'Emergency Contact' },
  { value: 'concussion', label: 'Concussion' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'public_profile_display', label: 'Public Information Sharing Agreement' },
  { value: 'other', label: 'Other' }
];

const ACCEPTANCE_MODES = [
  { value: 'accept', label: 'Digital Signature', description: 'Requires digital signature' },
  { value: 'acknowledge', label: 'Simple Acknowledgment', description: 'Simple checkbox confirmation' }
];

const TEMPLATE_TYPES = [
  {
    id: 'blank',
    title: 'Blank Template',
    description: 'Start with a blank template',
    category: 'other'
  },
  {
    id: 'waiver',
    title: 'Liability Waiver',
    description: 'Standard liability waiver template',
    category: 'waiver',
    previewContent: '<h2>Liability Waiver</h2><p>By signing this document, you acknowledge and agree to release and hold harmless the club from any liability...</p>'
  },
  {
    id: 'medical',
    title: 'Medical Form',
    description: 'Medical history and emergency contacts',
    category: 'medical',
    previewContent: '<h2>Medical Information</h2><p>Please provide the following medical information for emergency purposes...</p>'
  },
  {
    id: 'concussion',
    title: 'Concussion Protocol',
    description: 'Concussion awareness acknowledgment',
    category: 'concussion',
    previewContent: '<h2>Concussion Protocol</h2><p>I understand and acknowledge the risks of concussion and head injuries in sports...</p>'
  }
];

/**
 * Step 1: Template Settings
 *
 * Purpose: Configure basic template metadata and settings
 *
 * Captures:
 * - Template type/starter selection
 * - Title and description
 * - Category
 * - Acceptance mode
 * - Required/Active flags
 */
const Step1_TemplateSettings = ({
  wizardData,
  onDataChange,
  onValidationChange
}) => {
  const [errors, setErrors] = useState({});
  const [selectedTemplateType, setSelectedTemplateType] = useState(null);

  useEffect(() => {
    // Validate required fields
    const newErrors = {};

    if (!wizardData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    // Mark as valid if no errors
    const isValid = Object.keys(newErrors).length === 0 && wizardData.title?.trim();
    onValidationChange(isValid);
    setErrors(newErrors);
  }, [wizardData.title, onValidationChange]);

  const handleChange = (field, value) => {
    onDataChange({
      ...wizardData,
      [field]: value
    });
  };

  const handleTemplateTypeSelect = (templateType) => {
    setSelectedTemplateType(templateType.id);

    // Update wizard data with template defaults
    onDataChange({
      ...wizardData,
      title: templateType.title !== 'Blank Template' ? templateType.title : wizardData.title,
      category: templateType.category || wizardData.category,
      // Don't set content here - that's for step 2
      selectedTemplateType: templateType.id
    });
  };

  return (
    <div className="space-y-6">
      {/* Template Type Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Choose a Starting Point</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEMPLATE_TYPES.map(template => (
            <Button
              key={template.id}
              variant={selectedTemplateType === template.id ? 'primary' : 'outline'}
              onClick={() => handleTemplateTypeSelect(template)}
              className="text-left justify-start h-auto p-4"
            >
              <div>
                <div className="font-semibold">{template.title}</div>
                <div className="text-sm text-gray-600">{template.description}</div>
              </div>
            </Button>
          ))}
        </div>
      </div>

      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Template Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Title"
              name="title"
              value={wizardData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Liability Waiver"
              required
              error={errors.title}
            />
            <FormSelect
              label="Category"
              name="category"
              value={wizardData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              options={DOCUMENT_CATEGORIES}
              required
            />
          </div>

          <FormInput
            label="Description"
            name="description"
            value={wizardData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of this document"
          />
        </div>
      </div>

      {/* Configuration */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Configuration</h3>
        <div className="space-y-4">
          <FormRadioGroup
            label="Acceptance Mode"
            name="acceptance_mode"
            value={wizardData.acceptance_mode}
            onChange={(e) => handleChange('acceptance_mode', e.target.value)}
            options={ACCEPTANCE_MODES}
            required
          />

          <FormCheckbox
            label="Template is active and available for use"
            name="active"
            checked={wizardData.active}
            onChange={(e) => handleChange('active', e.target.checked)}
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
        <p className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ opacity: 0.7 }}>{getNavigationIcon('target')}</span>
          After configuring these settings, you'll design the template content in the next step.
        </p>
      </div>
    </div>
  );
};

export { Step1_TemplateSettings };
